import { useEffect, useState } from "react";
import { Info } from "lucide-react";
import OutflowGrid from "@/components/OutflowGrid";
import { api, formatApiError } from "@/lib/api";
import { formatCr, monthLabel } from "@/lib/format";
import { toast } from "sonner";

export default function MarketingExpenses() {
  return (
    <div className="space-y-5">
      <OutflowGrid
        page="marketing"
        testId="outflows-marketing"
        kicker="Outflows"
        title="Marketing Expenses"
        editRole="marketing"
        description="Aggregate roll-up row for the Cashflow ledger. Detailed Excel-sourced line items and read-only EPC Model Flat / SOH contributions are shown below."
      />
      <MarketingDetailSection />
    </div>
  );
}

function MarketingDetailSection() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/marketing/detail", { params: { horizon: 17 } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error("Marketing detail load failed", { description: formatApiError(e.response?.data?.detail) }));
  }, []);

  if (!data) return null;
  const { months, month_labels: labels, items, epc_items } = data;
  const all = [...(items || []), ...(epc_items || [])];

  return (
    <section className="cf-card p-4 md:p-5" data-testid="marketing-detail-section">
      <header className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-base text-ink font-bold">Marketing — Detailed Breakdown</h3>
          <p className="text-[11px] text-muted-dark">
            Sourced from the Marketing planning workbooks (Jul-Dec &apos;26 + Jan-Dec &apos;27). EPC Model Flat &amp; SOH rows are read-only and edited on the EPC tab.
          </p>
        </div>
        <div className="text-[11px] text-muted-dark">
          <div>
            Marketing total: <span className="font-bold text-ink num">{formatCr(data.total)}</span>
          </div>
          <div>
            EPC (read-only): <span className="font-bold text-ink num">{formatCr(data.epc_total)}</span>
          </div>
        </div>
      </header>

      {all.length === 0 && (
        <div className="text-center py-6 text-muted-dark">
          No detail rows loaded. Run the Marketing seeder or upload Excel files.
        </div>
      )}

      {all.length > 0 && (
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="min-w-full text-[11px] border-collapse" data-testid="marketing-detail-table">
            <thead className="sticky top-0 z-20" style={{ background: "var(--cf-surface-2)" }}>
              <tr>
                <th className="text-left px-2 py-2 uppercase tracking-widest text-[9.5px] text-muted-dark font-semibold sticky left-0 z-30 border-r border-hairline min-w-[220px]" style={{ background: "var(--cf-surface-2)" }}>
                  Line
                </th>
                {months?.map((m, i) => (
                  <th key={m} className="text-right px-2 py-2 uppercase tracking-widest text-[9.5px] text-muted-dark font-semibold whitespace-nowrap">
                    {labels?.[i] || monthLabel(m)}
                  </th>
                ))}
                <th className="text-right px-2 py-2 uppercase tracking-widest text-[9.5px] text-muted-dark font-semibold sticky right-0 z-30 border-l border-hairline" style={{ background: "var(--cf-surface-2)" }}>
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {items?.length > 0 && <SubHeader label="Marketing lines" span={months.length + 2} />}
              {items?.map((it) => (
                <DetailRow key={`m-${it.name}`} item={it} months={months} readOnly={false} />
              ))}
              {epc_items?.length > 0 && <SubHeader label="EPC — read-only (edited on EPC tab)" span={months.length + 2} />}
              {epc_items?.map((it) => (
                <DetailRow key={`e-${it.name}`} item={it} months={months} readOnly />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SubHeader({ label, span }) {
  return (
    <tr>
      <td colSpan={span} className="px-2 py-1 text-[9.5px] uppercase tracking-widest font-bold text-ink border-t border-hairline" style={{ background: "var(--cf-surface-2)" }}>
        {label}
      </td>
    </tr>
  );
}

function DetailRow({ item, months, readOnly }) {
  return (
    <tr className="border-t border-hairline/40 hover:bg-surface-2/40" data-testid={`marketing-detail-row-${item.name}`}>
      <td className="px-2 py-1.5 sticky left-0 z-10 border-r border-hairline/40 min-w-[220px]" style={{ background: "var(--cf-surface)" }}>
        <div className="text-ink font-medium truncate">{item.name}</div>
        <div className="text-[9.5px] uppercase tracking-widest text-muted-light flex items-center gap-1">
          {item.category || "—"}
          {readOnly && <Info size={9} />}
        </div>
      </td>
      {months.map((m) => {
        const v = item.values?.[m] || 0;
        return (
          <td key={m} className="text-right px-2 py-1.5 num tabular-nums whitespace-nowrap">
            {Math.abs(v) < 0.005 ? <span className="text-muted-light">–</span> : formatCr(v)}
          </td>
        );
      })}
      <td className="text-right px-2 py-1.5 num tabular-nums font-bold text-ink sticky right-0 z-10 border-l border-hairline/40" style={{ background: "var(--cf-surface)" }}>
        {formatCr(item.total)}
      </td>
    </tr>
  );
}
